import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useAppDispatch, useAppSelector, RootState } from '@store/store.tsx';
import { Address } from '@eshop/types/location.types.ts';
import { loadInitialCart, resetCartState } from '@store/slices/cartSlice.tsx';
import { fetchAddresses, markGuestPhoneVerified } from '@store/slices/userSlice.tsx';
import {
  setSelectedMethod,
  setShippingMethodId as setShippingMethodIdAction,
  clearMethods,
} from '@store/slices/paymentsSlice.tsx';
import { conns, connsPublic } from '@cfg/axiosConfig.tsx';
import { TotalsPaymentDiscount } from '@eshop/types/payment.types.ts';
import { CheckoutPayload, SaleOrderPreviewOut, SaleOrderConfirmOut } from '@eshop/types/order.types.ts';
import { selectCoupons } from '@store/slices/offersSlice.tsx';
import {
  selectContactVerificationRequired,
  selectGuestCheckoutSetting,
  selectSiteSettings,
} from '@store/slices/settingsSlice.tsx';
import { isAxiosError } from 'axios';
import { CART_EVENTS } from '@eshop/utils/cartEvents.ts';

type ContactStatus = 'idle' | 'collecting' | 'verified' | 'ready_unverified';

export type CheckoutStepId = 'identify' | 'delivery-type' | 'delivery-details' | 'payment' | 'review';
export type DeliveryType = 'shipping' | 'takeaway' | 'arrange' | null;

interface TakeawayAvailability {
  available: boolean;
  reasonCode?: 'takeaway_outside_schedule' | 'takeaway_closed_holiday' | 'takeaway_closed_closure';
  message?: string;
}

dayjs.extend(utc);
dayjs.extend(timezone);

interface CheckoutState {
  user: RootState['user']['profile'];
  coupons: string[];
  address?: Address;
  branch?: Address;
  shippingMethod?: number;
  shippingCost: number;
  paymentMethod?: number;
  takeaway: boolean;
  totals: TotalsPaymentDiscount;
  totalsStatus: 'idle' | 'loading' | 'ready' | 'error';
  comment: string;
  contactStatus: ContactStatus;
  checkoutStep: CheckoutStepId;
  deliveryType: DeliveryType;
}

const CLIENT_TOKEN_KEY = 'eshop_checkout_client_token';
const CHECKOUT_SNAPSHOT_KEY = 'eshop_checkout_snapshot';

interface CheckoutSnapshot {
  address?: Address;
  branch?: Address;
  shippingMethod?: number;
  shippingCost: number;
  paymentMethod?: number;
  takeaway: boolean;
  comment: string;
  deliveryType: DeliveryType;
}

const createClientToken = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

export const useCheckoutFlow = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.user.profile);
  const guestToken = useAppSelector((s) => s.user.guestToken);
  const guestAddresses = useAppSelector((s) => s.user.guestAddresses);
  const guestPhoneVerifiedAt = useAppSelector((s) => s.user.guestPhoneVerifiedAt);
  const guestRecovery = useAppSelector((s) => s.user.guestCheckoutRecovery);
  const recoveryReady = Boolean(guestRecovery && guestRecovery.expiresAt > Date.now() && guestRecovery.email === user?.email && guestRecovery.phone === user?.phone);
  useEffect(() => {
    if (!guestRecovery) return;
    const timer = window.setTimeout(() => dispatch(markGuestPhoneVerified(null)), Math.max(0, guestRecovery.expiresAt - Date.now()));
    return () => window.clearTimeout(timer);
  }, [dispatch, guestRecovery]);
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const cart = useAppSelector((s) => s.cart);
  const coupons = useAppSelector(selectCoupons).map((c) => c.code);
  const guestCheckoutSetting = useAppSelector(selectGuestCheckoutSetting);
  const contactVerificationRequired = useAppSelector(selectContactVerificationRequired);
  const siteSettings = useAppSelector(selectSiteSettings);
  const allowGuestCheckout = guestCheckoutSetting !== false;
  const effectiveGuestToken = allowGuestCheckout ? guestToken : undefined;

  const [address, setAddress] = useState<Address | undefined>();
  const [branch, setBranch] = useState<Address | undefined>();
  const [shippingMethod, setShippingMethodId] = useState<number | undefined>();
  const [shippingCost, setShippingCost] = useState<number>(0);
  const shippingSelectionRef = useRef<{
    initialized: boolean;
    id: number | undefined;
    cost: number;
  }>({
    initialized: false,
    id: undefined,
    cost: 0,
  });
  const [paymentMethod, setPaymentMethodId] = useState<number | undefined>();
  const [takeaway, setTakeaway] = useState<boolean>(cart.hasTakeaway);
  const [comment, setComment] = useState<string>('');
  const [contactStatus, setContactStatus] = useState<ContactStatus>('idle');
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStepId>('identify');
  const [deliveryType, setDeliveryType] = useState<DeliveryType>(null);
  const [takeawayAvailability, setTakeawayAvailability] = useState<TakeawayAvailability>({
    available: true,
  });
  const [totals, setTotals] = useState<TotalsPaymentDiscount>({
    total_previo: 0,
    descuento: 0,
    total_post: 0,
    order_discount: 0,
    shipping: 0,
    shipping_before: 0,
    shipping_discount: 0,
    shipping_discount_labels: [],
    order_discount_labels: [],
  });
  const [clientToken, setClientToken] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const stored = window.sessionStorage.getItem(CLIENT_TOKEN_KEY);
      if (stored) {
        return stored;
      }
      const generated = createClientToken();
      window.sessionStorage.setItem(CLIENT_TOKEN_KEY, generated);
      return generated;
    }
    return createClientToken();
  });

  const evaluateTakeawayAvailability = useCallback((): TakeawayAvailability => {
    const openingHours = siteSettings?.opening_hours;

    const safeString = (value: unknown): string | null => {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : null;
      }
      if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
      }
      return null;
    };

    if (!openingHours) {
      return { available: true };
    }

    const timezoneCandidate =
      safeString((openingHours as { timezone?: string | null })?.timezone) ??
      safeString(siteSettings?.timezone);

    let now = dayjs();
    if (timezoneCandidate) {
      try {
        if (typeof dayjs.tz === 'function') {
          now = dayjs().tz(timezoneCandidate);
        }
      } catch {
        now = dayjs();
      }
    }

    const parseDate = (value: unknown): string | null => {
      const str = safeString(value);
      return str && /^\d{4}-\d{2}-\d{2}$/.test(str) ? str : null;
    };

    const parseMinutesValue = (value: unknown): number | null => {
      const str = safeString(value);
      if (!str) return null;
      const match = str.match(/^(\d{2}):(\d{2})$/);
      if (!match) return null;
      const hours = Number(match[1]);
      const minutes = Number(match[2]);
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
      if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
      return hours * 60 + minutes;
    };

    const formatRange = (start: number, end: number) => {
      const startHours = Math.floor(start / 60);
      const startMinutes = start % 60;
      const endHours = Math.floor(end / 60);
      const endMinutes = end % 60;
      return `${String(startHours).padStart(2, '0')}:${String(startMinutes).padStart(2, '0')} - ${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
    };

    const currentDate = now.format('YYYY-MM-DD');
    const hoursRecord = ((openingHours ?? {}) as unknown) as Record<string, unknown>;

    const closures = Array.isArray((hoursRecord as any)?.closures)
      ? ((hoursRecord as any).closures as Array<Record<string, unknown>>)
      : [];
    for (const closure of closures) {
      const closureRecord = closure as Record<string, unknown>;
      const start = parseDate(closureRecord.start_date);
      const end = parseDate(closureRecord.end_date) ?? start;
      if (!start || !end) {
        continue;
      }
      if (start <= currentDate && currentDate <= end) {
        const label = safeString(closureRecord.label);
        return {
          available: false,
          reasonCode: 'takeaway_closed_closure',
          message: `El local está cerrado por ${label ?? 'un cierre programado'}.`,
        };
      }
    }

    const holidays = Array.isArray((hoursRecord as any)?.holidays)
      ? ((hoursRecord as any).holidays as Array<Record<string, unknown>>)
      : [];
    for (const holiday of holidays) {
      const holidayRecord = holiday as Record<string, unknown>;
      const holidayDate = parseDate(holidayRecord.date);
      if (!holidayDate) {
        continue;
      }
      if (holidayDate === currentDate) {
        const label = safeString(holidayRecord.label);
        const suffix = label ? ` ${label}` : '';
        return {
          available: false,
          reasonCode: 'takeaway_closed_holiday',
          message: `El local está cerrado por feriado${suffix}.`,
        };
      }
    }

    const weekdayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
    const dayKey = weekdayKeys[now.day()] ?? 'mon';
    const hasConfiguredWeeklyRanges = weekdayKeys.some((key) => {
      const weeklyRanges = Array.isArray((hoursRecord as any)?.[key])
        ? ((hoursRecord as any)[key] as Array<Record<string, unknown>>)
        : [];
      return weeklyRanges.some((range) => {
        const rangeRecord = range as Record<string, unknown>;
        const startMinutes = parseMinutesValue(rangeRecord.start);
        const endMinutes = parseMinutesValue(rangeRecord.end);
        return (
          startMinutes !== null &&
          endMinutes !== null &&
          startMinutes < endMinutes
        );
      });
    });
    const rawRanges = Array.isArray((hoursRecord as any)?.[dayKey])
      ? ((hoursRecord as any)[dayKey] as Array<Record<string, unknown>>)
      : [];

    const normalizedRanges: Array<[number, number]> = [];
    for (const range of rawRanges) {
      const rangeRecord = range as Record<string, unknown>;
      const startMinutes = parseMinutesValue(rangeRecord.start);
      const endMinutes = parseMinutesValue(rangeRecord.end);
      if (
        startMinutes === null ||
        endMinutes === null ||
        startMinutes >= endMinutes
      ) {
        continue;
      }
      normalizedRanges.push([startMinutes, endMinutes]);
    }

    if (normalizedRanges.length === 0) {
      if (!hasConfiguredWeeklyRanges) {
        return { available: true };
      }
      return {
        available: false,
        reasonCode: 'takeaway_outside_schedule',
        message: 'El retiro en tienda no está disponible hoy.',
      };
    }

    const currentMinutes = now.hour() * 60 + now.minute();
    for (const [start, end] of normalizedRanges) {
      if (start <= currentMinutes && currentMinutes < end) {
        return { available: true };
      }
    }

    const formattedRanges = normalizedRanges.map(([start, end]) => formatRange(start, end));
    return {
      available: false,
      reasonCode: 'takeaway_outside_schedule',
      message: `El retiro en tienda no está disponible en este horario. Horario de hoy: ${formattedRanges.join(', ')}.`,
    };
  }, [siteSettings?.opening_hours, siteSettings?.timezone]);

  useEffect(() => {
    if (isAuthenticated) {
      if (user?.email && user?.phone) {
        setContactStatus('verified');
      } else {
        setContactStatus('collecting');
      }
      return;
    }
    if (allowGuestCheckout) {
      if (user?.email && user?.phone && (guestPhoneVerifiedAt || !contactVerificationRequired)) {
        setContactStatus('verified');
      } else if (user?.email && user?.phone && recoveryReady) {
        setContactStatus('ready_unverified');
      } else {
        setContactStatus('collecting');
      }
      return;
    }
    setContactStatus('idle');
  }, [
    allowGuestCheckout,
    guestPhoneVerifiedAt,
    recoveryReady,
    contactVerificationRequired,
    isAuthenticated,
    user?.email,
    user?.phone,
  ]);

  useEffect(() => {
    dispatch(loadInitialCart());
  }, [dispatch]);

  useEffect(() => {
    if (!allowGuestCheckout) {
      return;
    }
    if (!isAuthenticated && effectiveGuestToken && guestAddresses.length === 0) {
      void dispatch(fetchAddresses());
    }
  }, [dispatch, allowGuestCheckout, effectiveGuestToken, guestAddresses.length, isAuthenticated]);

  useEffect(() => {
    setTakeaway(cart.hasTakeaway);
  }, [cart.hasTakeaway]);

  useEffect(() => {
    const updateAvailability = () => {
      setTakeawayAvailability(evaluateTakeawayAvailability());
    };
    updateAvailability();
    if (typeof window === 'undefined') {
      return;
    }
    const intervalId = window.setInterval(updateAvailability, 60_000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [evaluateTakeawayAvailability]);

  useEffect(() => {
    if (!takeawayAvailability.available && takeaway) {
      setTakeaway(false);
      setDeliveryType(null);
    }
  }, [takeawayAvailability.available, takeaway]);

  const [totalsStatus, setTotalsStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [totalsRetry, setTotalsRetry] = useState(0);
  const retryTotals = useCallback(() => setTotalsRetry((value) => value + 1), []);
  useEffect(() => {
    let cancelled = false;
    if (!cart.hasLoaded || !paymentMethod || (!takeaway && !shippingMethod)) {
      setTotalsStatus('idle');
      return;
    }
    setTotalsStatus('loading');
    const fetchTotals = async () => {
      try {
        const { data } = await connsPublic<TotalsPaymentDiscount>('eshop:payment:totals').post({
          takeaway, shipping_method: takeaway ? 'pickup' : deliveryType === 'arrange' ? 'to_agree' : 'delivery',
          shippingMethod, paymentMethod, shippingAddr: address, couponCodes: coupons,
        });
        if (!cancelled) {
          setTotals({ ...data });
          setTotalsStatus('ready');
        }
      } catch {
        if (!cancelled) setTotalsStatus('error');
      }
    };
    void fetchTotals();
    return () => { cancelled = true; };
  }, [shippingMethod, paymentMethod, takeaway, deliveryType, address, cart.total.total, cart.hasLoaded, totalsRetry, coupons.join('|')]);

  const setShippingMethod = useCallback(
    (id: number | undefined, cost: number) => {
      const previousSelection = shippingSelectionRef.current;
      const methodChanged = !previousSelection.initialized || previousSelection.id !== id;
      const costChanged = previousSelection.cost !== cost;

      if (!methodChanged && !costChanged) {
        return;
      }

      shippingSelectionRef.current = { initialized: true, id, cost };
      if (methodChanged) {
        setShippingMethodId(id);
      }
      if (costChanged) {
        setShippingCost(cost);
      }
      if (!methodChanged) {
        return;
      }

      setPaymentMethodId(undefined);
      dispatch(setSelectedMethod(undefined));
      dispatch(setShippingMethodIdAction(id));
      dispatch(clearMethods());
    },
    [dispatch]
  );

  const setPaymentMethod = useCallback(
    (id: number | undefined) => {
      setPaymentMethodId(id);
      dispatch(setSelectedMethod(id));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!takeaway) {
      return;
    }
    if (typeof shippingMethod === 'number' || shippingCost !== 0) {
      setShippingMethod(undefined, 0);
    }
  }, [takeaway, shippingMethod, shippingCost, setShippingMethod]);

  const refreshClientToken = useCallback(() => {
    const next = createClientToken();
    setClientToken(next);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(CLIENT_TOKEN_KEY, next);
    }
  }, []);

  const isGuestVerified = useCallback(() => {
    if (isAuthenticated) {
      return Boolean(user?.email && user?.phone);
    }
    return contactStatus === 'verified';
  }, [contactStatus, isAuthenticated, user?.email, user?.phone]);

  const hasVerifiedContact = useMemo(() => isGuestVerified() || contactStatus === 'ready_unverified', [isGuestVerified, contactStatus]);

  const isCheckoutReady = useCallback((): boolean => {
    const isPickup = takeaway || deliveryType === 'takeaway';
    const isArrange = deliveryType === 'arrange';
    const hasDelivery = isPickup
      ? true
      : isArrange
        ? typeof shippingMethod === 'number'
        : !!(address && shippingMethod);
    const hasActor = isAuthenticated || (allowGuestCheckout && effectiveGuestToken);
    return !!(
      hasDelivery &&
      paymentMethod &&
      hasVerifiedContact &&
      totalsStatus === 'ready' &&
      hasActor
    );
  }, [
    takeaway,
    branch,
    address,
    shippingMethod,
    paymentMethod,
    hasVerifiedContact,
    totalsStatus,
    allowGuestCheckout,
    effectiveGuestToken,
    isAuthenticated,
    deliveryType,
  ]);

  const getMissingSteps = useCallback((): string[] => {
    const missing: string[] = [];
    const isPickup = takeaway || deliveryType === 'takeaway';
    const isArrange = deliveryType === 'arrange';
    if (isPickup) {
      // Backend resolves the configured pickup address.
    } else if (isArrange) {
      if (!shippingMethod) missing.push('Debe elegir la entrega a coordinar.');
    } else {
      if (!address) missing.push('Debe seleccionar una dirección de envío.');
      if (!shippingMethod) missing.push('Debe elegir un método de envío.');
    }
    if (!paymentMethod) missing.push('Debe seleccionar un método de pago.');
    if (totalsStatus === 'error') missing.push('No pudimos calcular el total. Reintentá la cotización.');
    if (totalsStatus === 'loading') missing.push('Estamos calculando el total.');
    if (!hasVerifiedContact) {
      missing.push('Debes verificar tu WhatsApp ingresando el código que te enviamos.');
    }
    if (!isAuthenticated) {
      if (!allowGuestCheckout) {
        missing.push('Debes iniciar sesión para finalizar tu compra.');
      } else if (!effectiveGuestToken) {
        missing.push('Debes verificar tu WhatsApp antes de continuar.');
      }
    }
    return missing;
  }, [
    takeaway,
    branch,
    address,
    shippingMethod,
    paymentMethod,
    hasVerifiedContact,
    totalsStatus,
    allowGuestCheckout,
    effectiveGuestToken,
    isAuthenticated,
    deliveryType,
  ]);

  const buildPayload = (): CheckoutPayload => {
    const resolvedDeliveryType: DeliveryType = deliveryType ?? (takeaway ? 'takeaway' : 'shipping');
    const shipping_method =
      resolvedDeliveryType === 'takeaway'
        ? 'pickup'
        : resolvedDeliveryType === 'arrange'
          ? 'to_agree'
          : 'delivery';

    return {
      takeaway: shipping_method === 'pickup',
      shipping_method,
      shippingMethod,
      shippingAddr: shipping_method === 'delivery' ? address : undefined,
      paymentMethod,
      couponCodes: coupons,
      user: user ?? undefined,
      email: user?.email?.trim() || undefined,
      whatsapp: user?.phone ?? undefined,
      guestToken: effectiveGuestToken ?? undefined,
      fallback_token: recoveryReady ? guestRecovery?.token : undefined,
      client_token: clientToken,
      comment,
    };
  };

  const previewOrder = useCallback(
    async (payload = buildPayload()) => {
      const { data } = await (isAuthenticated ? conns : connsPublic)<SaleOrderPreviewOut>(
        'eshop:checkout:preview'
      ).post(payload);
      return data;
    },
    [takeaway, deliveryType, shippingMethod, address, paymentMethod, user, coupons, clientToken, comment, effectiveGuestToken, recoveryReady, guestRecovery]   // eslint-disable-line react-hooks/exhaustive-deps
  );

  const confirmOrder = useCallback(
    async (previewHash: string) => {
      const { data } = await (isAuthenticated ? conns : connsPublic)<SaleOrderConfirmOut>(
        'eshop:checkout:confirm'
      ).post({ ...buildPayload(), preview_hash: previewHash, accepted_summary: true });
      return data;
    },
    [takeaway, deliveryType, shippingMethod, address, paymentMethod, user, coupons, comment, refreshClientToken, clientToken, effectiveGuestToken, recoveryReady, guestRecovery]   // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onCheckoutSuccess = useCallback(async () => {
    try {
      await conns('eshop:cart:handler').delete();
    } catch (error) {
      if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status !== 404 && status !== 409) {
          // eslint-disable-next-line no-console
          console.warn('Failed to clear server cart after checkout', error);
        }
      } else {
        // eslint-disable-next-line no-console
        console.warn('Unexpected error clearing cart after checkout', error);
      }
    } finally {
      dispatch(resetCartState());
      if (guestRecovery) dispatch(markGuestPhoneVerified(null));
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(CART_EVENTS.CLOSE_DRAWER));
      }
      try {
        await dispatch(loadInitialCart());
      } catch (error) {
        if (isAxiosError(error)) {
          // eslint-disable-next-line no-console
          console.warn('Could not reload cart after checkout', error);
        } else {
          // eslint-disable-next-line no-console
          console.warn('Unexpected error reloading cart after checkout', error);
        }
      }
      refreshClientToken();
    }
  }, [dispatch, refreshClientToken, guestRecovery]);

  const state: CheckoutState = {
    user,
    coupons,
    address,
    branch,
    shippingMethod,
    shippingCost,
    paymentMethod,
    takeaway,
    totals,
    totalsStatus,
    comment,
    contactStatus,
    checkoutStep,
    deliveryType,
  };

  const requireGuestIdentify = useCallback(() => {
    if (isAuthenticated || !allowGuestCheckout) {
      return false;
    }
    return !hasVerifiedContact;
  }, [allowGuestCheckout, isAuthenticated, hasVerifiedContact]);

  const saveCheckoutSnapshot = useCallback(() => {
    if (typeof window === 'undefined') return;
    const snap: CheckoutSnapshot = {
      address,
      branch,
      shippingMethod,
      shippingCost,
      paymentMethod,
      takeaway,
      comment,
      deliveryType,
    };
    window.sessionStorage.setItem(CHECKOUT_SNAPSHOT_KEY, JSON.stringify(snap));
  }, [address, branch, shippingMethod, shippingCost, paymentMethod, takeaway, comment, deliveryType]);

  const restoreCheckoutSnapshot = useCallback(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    const raw = window.sessionStorage.getItem(CHECKOUT_SNAPSHOT_KEY);
    if (!raw) {
      return false;
    }
    window.sessionStorage.removeItem(CHECKOUT_SNAPSHOT_KEY);
    try {
      const parsed = JSON.parse(raw) as CheckoutSnapshot | null;
      if (!parsed || typeof parsed !== 'object') {
        return false;
      }
      let restored = false;
      if ('deliveryType' in parsed) {
        setDeliveryType(parsed.deliveryType ?? null);
        restored = true;
      }
      if (typeof parsed.takeaway === 'boolean') {
        setTakeaway(parsed.takeaway);
        restored = true;
      }
      if (parsed.address) {
        setAddress(parsed.address);
        restored = true;
      }
      if (parsed.branch) {
        setBranch(parsed.branch);
        restored = true;
      }
      if (typeof parsed.shippingMethod === 'number') {
        const restoredShippingCost = parsed.shippingCost ?? 0;
        shippingSelectionRef.current = {
          initialized: true,
          id: parsed.shippingMethod,
          cost: restoredShippingCost,
        };
        setShippingMethodId(parsed.shippingMethod);
        setShippingCost(restoredShippingCost);
        dispatch(setShippingMethodIdAction(parsed.shippingMethod));
        restored = true;
      }
      if (typeof parsed.paymentMethod === 'number') {
        setPaymentMethodId(parsed.paymentMethod);
        dispatch(setSelectedMethod(parsed.paymentMethod));
        restored = true;
      }
      if (typeof parsed.comment === 'string') {
        setComment(parsed.comment);
        restored = true;
      }
      return restored;
    } catch {
      // eslint-disable-next-line no-console
      console.warn('Could not restore checkout snapshot');
      return false;
    }
  }, [dispatch]);

  return {
    state,
    setTakeaway,
    setAddress,
    setBranch,
    setShippingMethod,
    setPaymentMethod,
    previewOrder,
    confirmOrder,
    isCheckoutReady,
    getMissingSteps,
    buildPayload,
    setComment,
    allowGuestCheckout,
    retryTotals,
    requireGuestIdentify,
    isGuestVerified,
    onCheckoutSuccess,
    takeawayAvailability,
    setCheckoutStep,
    setDeliveryType,
    saveCheckoutSnapshot,
    restoreCheckoutSnapshot,
    contactVerificationRequired,
  };
};

export type UseCheckoutFlowReturn = ReturnType<typeof useCheckoutFlow>;

type Nullable<T> = T | null | undefined;

interface UseAutoSelectSingleOptionParams<TOption, TValue> {
  options: Nullable<TOption[]>;
  selectedValue: Nullable<TValue>;
  onSelect: (value: TValue, option: TOption) => void;
  getOptionValue?: (option: TOption) => Nullable<TValue>;
  onAutoSelect?: (value: TValue, option: TOption) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

interface UseAutoSelectSingleOptionReturn {
  markManualSelection: () => void;
  resetManualSelection: () => void;
}

const microtask = (callback: () => void) => {
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(callback);
    return;
  }
  Promise.resolve()
    .then(callback)
    .catch(() => callback());
};

export const useAutoSelectSingleOption = <TOption, TValue = number | string>({
  options,
  selectedValue,
  onSelect,
  getOptionValue,
  onAutoSelect,
  isLoading = false,
  disabled = false,
}: UseAutoSelectSingleOptionParams<TOption, TValue>): UseAutoSelectSingleOptionReturn => {
  const manualOverrideRef = useRef(false);
  const previousCountRef = useRef<number>(Array.isArray(options) ? options.length : 0);
  const lastAutoValueRef = useRef<Nullable<TValue>>(null);

  useEffect(() => {
    const list = Array.isArray(options) ? options : [];
    const count = list.length;

    if (disabled || isLoading) {
      previousCountRef.current = count;
      return;
    }

    if (count !== 1) {
      if (count === 0) {
        lastAutoValueRef.current = null;
        manualOverrideRef.current = false;
      }
      previousCountRef.current = count;
      return;
    }

    const onlyOption = list[0];
    const value = (getOptionValue ? getOptionValue(onlyOption) : (onlyOption as Nullable<TValue>)) ?? null;
    if (value === null) {
      previousCountRef.current = count;
      return;
    }

    if (selectedValue === value) {
      lastAutoValueRef.current = value;
      previousCountRef.current = count;
      manualOverrideRef.current = false;
      return;
    }

    const prevCount = previousCountRef.current;
    const blockedByManualOverride =
      manualOverrideRef.current && prevCount === count && lastAutoValueRef.current !== value;
    if (blockedByManualOverride) {
      previousCountRef.current = count;
      return;
    }

    lastAutoValueRef.current = value;
    manualOverrideRef.current = false;
    previousCountRef.current = count;
    onSelect(value, onlyOption);
    if (onAutoSelect) {
      microtask(() => onAutoSelect(value, onlyOption));
    }
  }, [
    options,
    selectedValue,
    getOptionValue,
    onSelect,
    onAutoSelect,
    isLoading,
    disabled,
  ]);

  const markManualSelection = useCallback(() => {
    manualOverrideRef.current = true;
  }, []);

  const resetManualSelection = useCallback(() => {
    manualOverrideRef.current = false;
  }, []);

  return { markManualSelection, resetManualSelection };
};
